/**
 * assigneeOption.tsx
 * Shared rendering + data for the employee-assignment pickers so the inline
 * dropdown (InlineAssigneePicker) and the advanced dialog (AssignEmployeesDialog)
 * show candidates the SAME way: one row — name, skill level, tier — with the
 * person's availability (existing load for the task's week) on the right.
 *
 *   AssigneeOptionRow   — the single-row option renderer.
 *   TierChip            — the tier pill (moved here so both surfaces share it).
 *   useAssigneeCapacity — fetch per-(user) weekly load for the task's week,
 *                         cached per (week, scenario) across all picker rows.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import { Warning } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { SkillLevelChip, SKILL_LEVEL_SHORT, SKILL_LEVEL_LABELS } from './SkillLevelChip';
import { spareCapacityHint } from '../../utils/rosterAllocation';
import {
  fetchEmployeeWeeks,
  formatCrossScenarioSource,
  type CrossScenarioSource,
} from '../../services/scheduleEmployeeSchedule';
import { onScheduleChange } from '../../services/scheduleBus';
import {
  isContinuousRun,
  continuousAttendanceWindows,
} from '../../utils/runModeRendering';
import { useScheduleHolidays } from '../../hooks/useScheduleHolidays';

// ── Availability data ─────────────────────────────────────────────────────────

/**
 * Wave 3 (percent retirement): availability is DAY-based. A person's load on
 * the booking's window reads as busy-days-of-window (Q5B family), a day off in
 * the window is "unavailable" (the save would 409 ABSENCE_DAY_CONFLICT — Q3B),
 * and a soft hold is a boolean ("held in another plan", no % number).
 */
export interface AssigneeLoad {
  /** Busy working days within the booking's own window (tasks or absences). */
  busyDaysOnBookingDays: number;
  /** Working days in the booking's window (from the wire's byDayTaskCount keys). */
  windowWorkingDays: number;
  /** True when a DATED ABSENCE covers one of the booking's days — assigning
   *  would hard-409 (Q3B), so the row reads "unavailable (day off)". */
  absentOnBookingDays: boolean;
  /** True when another DRAFT plan tentatively holds this person on these days. */
  softHeldOnBookingDays: boolean;
  /** The OTHER draft scenarios holding this person, for attribution in the chip/toast. */
  softSources: CrossScenarioSource[];
  /**
   * Staffing Allocation % (PR B — D6 "suggest who's free"): the person's Σ
   * weekly allocation % (share of their time across ALL their WO/quote work)
   * for the target week — the least-loaded-first sort key + the "· 30% · lots
   * free" annotation. 0 when free / unknown. Advisory only.
   */
  allocationPercent: number;
}

/**
 * The raw per-user load we cache from the API: the per-day breakdowns UNIONED
 * across every fetched week (P-18 multi-week). The booking-day projection is
 * derived from the maps at consumption time so the SAME cached window serves
 * bookings with different day-windows without a re-fetch.
 */
interface RawWeekLoad {
  /** Legacy per-day load (busy = 100) — fallback busy signal. */
  byDay: Record<string, number>;
  /** Wave-3 additive: working day → task count (0-count days included). */
  byDayTaskCount: Record<string, number>;
  /** Wave-3 additive: working day → covered by a dated absence. */
  byDayAbsent: Record<string, boolean>;
  /** Cross-scenario soft load per day (other drafts' tentative holds). */
  byDaySoft: Record<string, number>;
  /** Other draft scenarios holding this person this week (attribution). */
  softSources: CrossScenarioSource[];
  /** Σ weekly allocation % (PR B — D6): the MAX across the fetched weeks (worst
   *  case for "how loaded are they"). 0 when the feed carries no allocation. */
  allocationPercent: number;
}

// Cache keyed by `${firstSat}|${weekCount}|${scenarioKey}` → (userKey → merged
// load). Shared across every picker row so a window is fetched at most once.
const capacityCache = new Map<string, Map<string, RawWeekLoad>>();

/** Add one day to an ISO 'YYYY-MM-DD' (UTC, DST-safe). */
function addOneDayIso(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/** Add N days to an ISO 'YYYY-MM-DD' (UTC, DST-safe). */
function addDaysIsoN(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * Wave-3 day-based window scan across [startIso, endIso] inclusive:
 *   - workingDays: days the wire knows as working days (byDayTaskCount carries
 *     0-count entries for EVERY working day of a fetched week);
 *   - busyDays: working days with ≥1 task, a dated absence, or legacy load;
 *   - absent: any dated-absence day in the window (assigning would 409 — Q3B);
 *   - soft: any cross-scenario tentative hold in the window (advisory).
 */
function scanWindow(
  raw: RawWeekLoad,
  startIso: string,
  endIso: string,
): { busyDays: number; workingDays: number; absent: boolean; soft: boolean } {
  let busyDays = 0;
  let workingDays = 0;
  let absent = false;
  let soft = false;
  let d = startIso;
  // Guard against a pathological range so a bad date never spins the loop.
  for (let guard = 0; d <= endIso && guard < 400; guard++) {
    const tc = raw.byDayTaskCount[d];
    const isWorking = tc !== undefined || (raw.byDay[d] ?? 0) > 0;
    if (isWorking) {
      workingDays += 1;
      const dayAbsent = raw.byDayAbsent[d] === true;
      if ((tc ?? 0) > 0 || dayAbsent || (raw.byDay[d] ?? 0) > 0) busyDays += 1;
      if (dayAbsent) absent = true;
    }
    if ((raw.byDaySoft[d] ?? 0) > 0) soft = true;
    d = addOneDayIso(d);
  }
  return { busyDays, workingDays, absent, soft };
}

/** Drop all cached availability — call after any booking/assignment change so the
 *  next dropdown open re-fetches fresh per-day load (no more stale "original date"
 *  availability). Exported for explicit invalidation (e.g. on Phase-2 entry). */
export function clearCapacityCache(): void {
  capacityCache.clear();
}

// The picker availability is the #1 stale-data source after a date change: this
// module-level cache used to live for the whole page session. Flush it whenever a
// booking or assignment changes ANYWHERE (the same scheduleBus the schedule views
// already use), so the dropdown + the per-day numbers always reflect the current
// schedule. One app-lifetime subscriber — no React lifecycle coupling. (Gigi, 2026-06-22)
if (typeof window !== 'undefined') {
  onScheduleChange((c) => {
    if (c.kind === 'bookings-changed' || c.kind === 'assignments-changed') {
      capacityCache.clear();
    }
  });
}

/** The Saturday on or before an ISO date (BST week start). */
function satOnOrBefore(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - ((dt.getUTCDay() + 1) % 7));
  return dt.toISOString().slice(0, 10);
}

/** The Friday (week end) of a BST week given its Saturday start. */
function weekFridayIso(sat: string): string {
  const [y, m, d] = sat.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 6)).toISOString().slice(0, 10);
}

interface CapacityBooking {
  MachineBookingKey: string;
  StartDate: string;
  /** Booking end (inclusive) — needed so availability reflects the booking's actual days. */
  EndDate: string;
}

/** Whole weeks (inclusive of both Saturdays) between two BST week-start dates. */
function weeksBetweenSats(firstSat: string, lastSat: string): number {
  const [y1, m1, d1] = firstSat.split('-').map(Number);
  const [y2, m2, d2] = lastSat.split('-').map(Number);
  const ms = Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1);
  return Math.max(0, Math.round(ms / (7 * 86_400_000)));
}

/** Server fetch is bounded: never ask for more than 12 weeks of availability. */
const MAX_CAPACITY_WEEKS = 12;

/**
 * Resolve the task's relevant BST weeks and fetch every candidate's existing load
 * across them. With a `weekStart` override (Phase-2 per-week staffing) exactly ONE
 * week is judged; otherwise EVERY week the booking spans is fetched in one call
 * (bounded at 12) — so a person busy only in week 2 of a 3-week test no longer
 * reads "free" (P-18). The availability is then projected onto the BOOKING's own
 * working days (StartDate→EndDate) — not whole weeks — so a Thu–Fri test reads
 * availability for Thu/Fri, ignoring an unrelated Monday booking. Wave 3: the
 * projection is DAY-based (busy X of Y days / unavailable on a day off / held in
 * another plan) — no percents.
 *
 * `availabilityUnknown` is true while the load is NOT trustworthy: before the
 * first response arrives AND after a failed fetch (P-18) — the rows must render a
 * neutral "unknown" state instead of a false green "free". The save-time
 * absence-day gate (Q3B) remains authoritative either way.
 */
export function useAssigneeCapacity(params: {
  open: boolean;
  bookings: CapacityBooking[] | undefined;
  machineBookingKey?: string | null;
  scenarioKey?: string;
  /**
   * Per-week override (2026-06-23): the BST Saturday of the cell being staffed.
   * When set, availability is judged for THIS week (the booking days clamped to it),
   * not the whole booking span — so a Phase-2 weekly cell shows that week's load.
   */
  weekStart?: string;
  /**
   * RM-5: the task's RunMode snapshot. For a CONTINUOUS task, availability is
   * judged on the ATTENDANCE windows (the FE mirror of
   * continuousStaffingWindows). Lab answer Q2 (2026-07-10): those windows now
   * cover EVERY working day of [setup..teardown] — full-duration staffing —
   * so the picker judges the same days the save gate does (a mid-run day off
   * legitimately counts against availability now).
   */
  runMode?: string | null;
  /** Prep steps are never continuous (RM-5 guard, mirrors the backend). */
  isPrepStep?: boolean;
}): { relevantWeek: string | null; capacity: Map<string, AssigneeLoad>; availabilityUnknown: boolean } {
  const { open, bookings, machineBookingKey, scenarioKey, weekStart, runMode, isPrepStep } = params;

  // The single booking we're staffing: the named machine's booking, else the
  // earliest-starting one. Its [StartDate, EndDate] is the day-window we judge
  // availability against.
  const targetBooking = useMemo(() => {
    const list = bookings ?? [];
    if (list.length === 0) return null;
    if (machineBookingKey) {
      const m = list.find((b) => b.MachineBookingKey === machineBookingKey);
      if (m) return m;
    }
    return list.reduce((min, b) => (b.StartDate < min.StartDate ? b : min), list[0]);
  }, [bookings, machineBookingKey]);

  const relevantWeek = useMemo(
    () => weekStart ?? (targetBooking ? satOnOrBefore(targetBooking.StartDate) : null),
    [targetBooking, weekStart],
  );
  const bookingStart = targetBooking?.StartDate ?? null;
  const bookingEnd = targetBooking?.EndDate ?? null;

  // RM-5: the continuous attendance windows (full duration — every working
  // day of [setup..teardown], lab answer Q2 2026-07-10). Holidays are fetched
  // only for continuous tasks (a small buffered range past the end — the
  // teardown day can be pushed past weekend + stat holidays); before the
  // holiday response lands the windows are weekend-only approximations.
  const continuous = !isPrepStep && isContinuousRun(runMode ?? null);
  const { holidays: holidayEntries } = useScheduleHolidays(
    continuous && bookingStart ? bookingStart : '',
    continuous && bookingEnd ? addDaysIsoN(bookingEnd, 21) : '',
  );
  const holidayDates = useMemo(
    () => holidayEntries.map((h) => h.date.slice(0, 10)),
    [holidayEntries],
  );
  const continuousWindows = useMemo(
    () =>
      continuous && bookingStart && bookingEnd
        ? continuousAttendanceWindows(bookingStart, bookingEnd, holidayDates)
        : null,
    [continuous, bookingStart, bookingEnd, holidayDates],
  );
  // The teardown day can fall AFTER the booking's calendar end (Saturday end →
  // Monday teardown) — the fetch window must reach it or the teardown week's
  // per-day load would be missing.
  const capacityEnd = useMemo(() => {
    if (!bookingEnd) return null;
    const lastWindowEnd = continuousWindows?.[continuousWindows.length - 1]?.endDate;
    return lastWindowEnd && lastWindowEnd > bookingEnd ? lastWindowEnd : bookingEnd;
  }, [bookingEnd, continuousWindows]);

  // How many weeks to fetch: 1 with a per-week override, else every week the
  // booking spans (bounded). The window's LAST Saturday drives the projection clamp.
  const { fetchWeeks, lastSat } = useMemo(() => {
    if (!relevantWeek) return { fetchWeeks: 1, lastSat: null as string | null };
    if (weekStart) return { fetchWeeks: 1, lastSat: relevantWeek };
    if (!capacityEnd) return { fetchWeeks: 1, lastSat: relevantWeek };
    const endSat = satOnOrBefore(capacityEnd);
    const n = Math.min(MAX_CAPACITY_WEEKS, weeksBetweenSats(relevantWeek, endSat) + 1);
    // The clamp must match what was actually FETCHED (n may be capped at 12).
    const [y, m, d] = relevantWeek.split('-').map(Number);
    const cappedLast = new Date(Date.UTC(y, m - 1, d + (n - 1) * 7)).toISOString().slice(0, 10);
    return { fetchWeeks: n, lastSat: cappedLast };
  }, [relevantWeek, weekStart, capacityEnd]);

  // Raw merged load per user (max week total + unioned byDay across the fetched
  // window), cached per (firstSat, weekCount, scenario) so the projection onto a
  // specific booking-window is a cheap re-derive, not a re-fetch.
  const [rawWeek, setRawWeek] = useState<Map<string, RawWeekLoad>>(() => new Map());
  // P-18: "not yet loaded" must read the same as "failed" — while the fetch is in
  // flight rawWeek is empty and rows would falsely read green "free" (review note).
  const [loadFailed, setLoadFailed] = useState(true);
  useEffect(() => {
    if (!open || !relevantWeek) return;
    const ck = `${relevantWeek}|${fetchWeeks}|${scenarioKey ?? ''}`;
    const cached = capacityCache.get(ck);
    if (cached) {
      setRawWeek(cached);
      setLoadFailed(false);
      return;
    }
    let cancelled = false;
    setLoadFailed(true); // loading == unknown until the response lands
    fetchEmployeeWeeks({ startWeek: relevantWeek, weekCount: fetchWeeks, ...(scenarioKey ? { scenarioKey } : {}) })
      .then((resp) => {
        const sourcesByUser = resp.crossScenarioSources ?? {};
        const map = new Map<string, RawWeekLoad>();
        for (const c of resp.cells) {
          // Merge every returned week per user: the per-day map keys are ISO
          // dates, so a plain union is exact.
          const prev = map.get(c.userKey);
          if (prev) {
            Object.assign(prev.byDay, c.byDay ?? {});
            Object.assign(prev.byDayTaskCount, c.byDayTaskCount ?? {});
            Object.assign(prev.byDayAbsent, c.byDayAbsent ?? {});
            Object.assign(prev.byDaySoft, c.byDaySoft ?? {});
            prev.allocationPercent = Math.max(prev.allocationPercent, c.allocationPercent ?? 0);
          } else {
            map.set(c.userKey, {
              byDay: { ...(c.byDay ?? {}) },
              byDayTaskCount: { ...(c.byDayTaskCount ?? {}) },
              byDayAbsent: { ...(c.byDayAbsent ?? {}) },
              byDaySoft: { ...(c.byDaySoft ?? {}) },
              softSources: sourcesByUser[c.userKey.toLowerCase()] ?? [],
              allocationPercent: c.allocationPercent ?? 0,
            });
          }
        }
        capacityCache.set(ck, map);
        if (!cancelled) {
          setRawWeek(map);
          setLoadFailed(false);
        }
      })
      .catch(() => {
        // Availability is best-effort — but a swallowed failure must NOT render
        // as green "free" (P-18): flag it so rows show "availability unknown".
        if (!cancelled) setLoadFailed(true);
      });
    return () => { cancelled = true; };
  }, [open, relevantWeek, fetchWeeks, scenarioKey]);

  // Project each candidate's merged load onto the booking's own days, clamped to
  // the FETCHED window [relevantWeek, Friday(lastSat)] — the whole booking span in
  // the default flow, exactly one week with a weekStart override. RM-5: for a
  // CONTINUOUS task the projection spans are the attendance windows — every
  // working day of [setup..teardown] (lab answer Q2, 2026-07-10) — so
  // "busy X of Y days" and the day-off disable reflect the days the save gate
  // actually judges (the teardown week can extend past the raw booking end).
  const capacity = useMemo(() => {
    const out = new Map<string, AssigneeLoad>();
    if (!bookingStart || !bookingEnd || !relevantWeek || !lastSat) return out;
    const windowFri = weekFridayIso(lastSat);
    const spans = continuousWindows
      ? continuousWindows.map((w) => ({ start: w.startDate, end: w.endDate }))
      : [{ start: bookingStart, end: bookingEnd }];
    const clamped = spans
      .map((s) => ({
        start: s.start > relevantWeek ? s.start : relevantWeek,
        end: s.end < windowFri ? s.end : windowFri,
      }))
      .filter((s) => s.start <= s.end);
    if (clamped.length === 0) return out; // no judged days reach this window
    for (const [userKey, raw] of rawWeek) {
      let busyDays = 0;
      let workingDays = 0;
      let absent = false;
      let soft = false;
      for (const s of clamped) {
        const scan = scanWindow(raw, s.start, s.end);
        busyDays += scan.busyDays;
        workingDays += scan.workingDays;
        absent = absent || scan.absent;
        soft = soft || scan.soft;
      }
      out.set(userKey, {
        busyDaysOnBookingDays: busyDays,
        windowWorkingDays: workingDays,
        absentOnBookingDays: absent,
        softHeldOnBookingDays: soft,
        softSources: raw.softSources,
        allocationPercent: raw.allocationPercent,
      });
    }
    return out;
  }, [rawWeek, bookingStart, bookingEnd, relevantWeek, lastSat, continuousWindows]);

  return { relevantWeek, capacity, availabilityUnknown: loadFailed };
}

// ── Tier pill ─────────────────────────────────────────────────────────────────

export function TierChip({ tier }: { tier: string | null }) {
  const theme = useTheme();
  if (!tier) {
    return (
      <Tooltip title="No Tier set. Set Tier in Admin → Users.">
        <Chip
          icon={<Warning sx={{ fontSize: '14px !important' }} />}
          label="No tier"
          size="small"
          sx={{
            fontSize: '0.68rem',
            height: 20,
            backgroundColor: alpha(theme.schedule.conflictSoft, 0.12),
            color: theme.schedule.conflictSoft,
          }}
        />
      </Tooltip>
    );
  }
  const color =
    tier === 'Lead' || tier === 'Senior'
      ? theme.schedule.maintenance
      : alpha(theme.palette.primary.main, 0.7);
  return (
    <Chip
      label={tier}
      size="small"
      sx={{ fontSize: '0.68rem', height: 20, backgroundColor: alpha(color, 0.12), color, fontWeight: 600 }}
    />
  );
}

// ── Option row ────────────────────────────────────────────────────────────────

export interface AssigneeOptionRowProps {
  displayName: string;
  tier: string | null;
  /** Skill level for the task's task type (1–5), null = unrated. */
  skillLevel?: number | null;
  /** Whether to show the skill chip (true when the task has a task type). */
  showSkill: boolean;
  /** Existing load for the task's week, or null when unknown/free. */
  load: AssigneeLoad | null;
  /** Whether to show the availability column (true when the task's week is known). */
  showLoad: boolean;
  /**
   * P-18: true while the availability fetch is loading or has FAILED — render a
   * neutral "availability unknown" instead of a false green "free". Never show
   * the free state when this is set.
   */
  loadUnknown?: boolean;
}

/**
 * One picker option, on a single row:
 *   [name]  [skill#]  [tier]   ……spacer……   [day bar + "free / busy X of Y days / unavailable"]
 */
export const AssigneeOptionRow: React.FC<AssigneeOptionRowProps> = ({
  displayName, tier, skillLevel, showSkill, load, showLoad, loadUnknown,
}) => {
  const theme = useTheme();
  // Availability is judged on the BOOKING's own days (Thu/Fri), not the whole week —
  // so a person busy on an unrelated Monday reads "free" for a Thu–Fri task.
  // Wave 3: day-based — busy X of Y days; a day off in the window = unavailable
  // (assigning would hard-409, Q3B).
  const busy = load?.busyDaysOnBookingDays ?? 0;
  const windowDays = load?.windowWorkingDays ?? 0;
  const unavailable = load?.absentOnBookingDays ?? false;
  const busyFraction = windowDays > 0 ? busy / windowDays : 0;
  const band = unavailable
    ? theme.palette.error.main
    : busy > 0 && busyFraction >= 1
      ? theme.palette.warning.main
      : theme.palette.success.main;
  const availLabel = unavailable
    ? 'unavailable (day off)'
    : busy === 0
      ? 'free'
      : windowDays > 0
        ? `busy ${busy} of ${windowDays} day${windowDays !== 1 ? 's' : ''}`
        : 'busy';

  // Cross-scenario SOFT hold — another DRAFT plan already pencils this person in on
  // these days. Advisory only (the hard band above is untouched): the planner can
  // still book, but should know there's a chance of a clash. Orange = soft conflict.
  const softSources = load?.softSources ?? [];
  const hasSoft = showLoad && (load?.softHeldOnBookingDays ?? false);
  const softTip = hasSoft
    ? `Also held in another plan on these days${softSources.length ? ` — ${softSources.map(formatCrossScenarioSource).join('; ')}` : ''}. Tentative, so it may not happen — book if you need them, just know there's a chance of a clash.`
    : '';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
      <Typography
        sx={{ fontSize: '0.82rem', minWidth: 0, flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {displayName}
      </Typography>

      {showSkill && (skillLevel != null ? (
        <Tooltip
          title={`Skill: ${SKILL_LEVEL_SHORT[skillLevel] ?? `L${skillLevel}`} — ${SKILL_LEVEL_LABELS[skillLevel] ?? ''}`}
          arrow
        >
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            <SkillLevelChip level={skillLevel} pixelSize={20} />
          </Box>
        </Tooltip>
      ) : (
        <Tooltip title="No skill level recorded for this task type" arrow>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'text.disabled', flexShrink: 0, minWidth: 20, textAlign: 'center' }}>
            —
          </Typography>
        </Tooltip>
      ))}

      <Box sx={{ flexShrink: 0 }}><TierChip tier={tier} /></Box>

      <Box sx={{ flex: 1 }} />

      {hasSoft && (
        <Tooltip title={softTip} arrow>
          <Chip
            icon={<Warning sx={{ fontSize: '12px !important' }} />}
            size="small"
            label="held in another plan"
            sx={{
              flexShrink: 0,
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 700,
              backgroundColor: alpha(theme.schedule.conflictSoft, 0.14),
              color: theme.schedule.conflictSoft,
              border: `1px solid ${alpha(theme.schedule.conflictSoft, 0.5)}`,
              '& .MuiChip-icon': { color: theme.schedule.conflictSoft, ml: 0.4 },
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        </Tooltip>
      )}

      {/* Staffing Allocation % (D6 "suggest who's free"): the person's weekly
          utilization + spare-capacity phrase, e.g. "30% · lots free" / "125% ·
          over". Advisory — never disables the option. */}
      {showLoad && !loadUnknown && (load?.allocationPercent ?? 0) > 0 && (
        <Tooltip title={`Currently ${Math.round(load!.allocationPercent)}% allocated this week across all their work`} arrow>
          <Typography
            sx={{
              fontSize: '0.62rem',
              fontWeight: 700,
              flexShrink: 0,
              whiteSpace: 'nowrap',
              color: load!.allocationPercent > 100
                ? theme.schedule.allocOver
                : load!.allocationPercent >= 86
                  ? theme.schedule.allocAt
                  : theme.schedule.employeeAlloc,
            }}
          >
            {Math.round(load!.allocationPercent)}% · {spareCapacityHint(load!.allocationPercent)}
          </Typography>
        </Tooltip>
      )}

      {showLoad && loadUnknown && (
        // P-18: the fetch failed (or hasn't resolved) — a green "free" here would
        // be a lie. Neutral grey, never the free state; the save-time gate still applies.
        <Tooltip
          title="Could not load current bookings — the day-off check at save still applies."
          arrow
        >
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'text.disabled', whiteSpace: 'nowrap', flexShrink: 0 }}>
            availability unknown
          </Typography>
        </Tooltip>
      )}

      {showLoad && !loadUnknown && (
        <Tooltip
          title={unavailable
            ? "Has a day off (vacation/sick/field work) on this task's days — assigning here is blocked"
            : busy === 0
              ? "Free on this task's days — nothing booked yet"
              : `Already busy ${busy} of ${windowDays} day${windowDays !== 1 ? 's' : ''} of this task's window — tasks can stack on a busy day`}
          arrow
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Box sx={{ width: 40, height: 5, borderRadius: 3, bgcolor: alpha(theme.palette.text.primary, 0.08), overflow: 'hidden' }}>
              <Box sx={{ width: `${Math.min(100, Math.round(busyFraction * 100))}%`, height: '100%', bgcolor: band }} />
            </Box>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: band, whiteSpace: 'nowrap', minWidth: 84, textAlign: 'right' }}>
              {availLabel}{unavailable ? ' ⚠' : ''}
            </Typography>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
